export const INTERSTITIAL_PLACEMENTS = [
    'interstitial_betwon_dialog',
    'interstitial_minigame',
    'interstitial_dailybonus_close_dialog',
    'interstitial_battle_result',
    'interstitial_flashbet_round_end',
] as const;

export type InterstitialPlacementName = typeof INTERSTITIAL_PLACEMENTS[number];

export const INTERSTITIAL_PLACEMENT_LABELS: Record<InterstitialPlacementName, string> = {
    interstitial_betwon_dialog: 'Bet won dialog',
    interstitial_minigame: 'Minigame win',
    interstitial_dailybonus_close_dialog: 'Daily bonus close',
    interstitial_battle_result: 'Battle result close',
    interstitial_flashbet_round_end: 'Flash Bet round end',
};

/** Short plain-language hint under each moment switch in the dashboard. */
export const INTERSTITIAL_PLACEMENT_HINTS: Record<InterstitialPlacementName, string> = {
    interstitial_betwon_dialog: 'After the user closes a won-bet result dialog (not if they just watched a rewarded video there).',
    interstitial_minigame: 'After a minigame win, every N wins (see field below).',
    interstitial_dailybonus_close_dialog: 'When the daily bonus dialog is dismissed.',
    interstitial_battle_result: 'When closing a finished battle result.',
    interstitial_flashbet_round_end: 'When closing Flash Bet details from a finished round.',
};

/** Sensible defaults for a newly added segment (not live until Save). */
export function defaultNewSegment(existingIds: string[]): InterstitialPolicyRule {
    return {
        id: uniquePolicyId('segment', existingIds),
        enabled: true,
        includeTags: [],
        excludeTags: [],
        interstitialsEnabled: true,
        combineWithOthers: false,
        global: {
            minGapSeconds: 60,
            maxPerSession: 0,
            maxPerDay: 0,
            signupGraceMinutes: 0,
            requireFirstBet: false,
            rewardedQuietMinutes: 10,
        },
        placements: {
            interstitial_betwon_dialog: { enabled: true, cooldownMinutes: 5 },
            interstitial_minigame: { enabled: true, cooldownMinutes: 0, everyNWins: 3 },
            interstitial_dailybonus_close_dialog: { enabled: false, cooldownMinutes: 0 },
            interstitial_battle_result: { enabled: false, cooldownMinutes: 0 },
            interstitial_flashbet_round_end: { enabled: false, cooldownMinutes: 0 },
        },
    };
}

export function uniquePolicyId(base: string, existingIds: string[]): string {
    const taken = new Set(existingIds.map(id => id.toLowerCase()));
    const slug = slugifyPolicyId(base) || 'segment';
    if (!taken.has(slug)) return slug;
    let n = 2;
    while (taken.has(`${slug}_${n}`)) n += 1;
    return `${slug}_${n}`;
}

export function slugifyPolicyId(raw: string): string {
    return raw
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 64);
}

export interface InterstitialPolicyGlobal {
    minGapSeconds: number;
    maxPerSession: number;
    maxPerDay: number;
    signupGraceMinutes: number;
    requireFirstBet: boolean;
    rewardedQuietMinutes: number;
}

export interface InterstitialPolicyPlacement {
    enabled: boolean;
    cooldownMinutes: number;
    everyNWins?: number;
}

export interface InterstitialPolicyRule {
    id: string;
    enabled: boolean;
    includeTags: string[];
    excludeTags: string[];
    interstitialsEnabled: boolean;
    /**
     * Ads-on only. When true, matching continues and placements merge with other combine segments.
     * When false (default), this match stops the cascade (exclusive).
     */
    combineWithOthers?: boolean;
    global?: InterstitialPolicyGlobal | null;
    /** Built-in `interstitial_*` moments and/or analytics EventType names (e.g. ShopClosed). */
    placements?: Partial<Record<string, InterstitialPolicyPlacement>>;
}

export function isBuiltInPlacement(name: string): name is InterstitialPlacementName {
    return (INTERSTITIAL_PLACEMENTS as readonly string[]).includes(name);
}

export interface InterstitialPolicyExperiment {
    enabled: boolean;
    holdoutPercent: number;
    salt: string;
}

export interface InterstitialPolicyDocument {
    version: number;
    globalEnabled: boolean;
    experiment: InterstitialPolicyExperiment;
    defaultPolicyId: string;
    policies: InterstitialPolicyRule[];
}

export interface InterstitialPoliciesBundle {
    config: InterstitialPolicyDocument;
    persisted: boolean;
    canRollback: boolean;
    warnings: string[];
}

export interface InterstitialPolicyPreview {
    betupId: number;
    found: boolean;
    tags: string[];
    policyId: string;
    group: string;
    interstitialsEnabled: boolean;
    summary: string;
}

export function emptyGlobal(rewardedQuietMinutes = 0): InterstitialPolicyGlobal {
    return {
        minGapSeconds: 0,
        maxPerSession: 0,
        maxPerDay: 0,
        signupGraceMinutes: 0,
        requireFirstBet: false,
        rewardedQuietMinutes,
    };
}

export function emptyPlacements(): Record<InterstitialPlacementName, InterstitialPolicyPlacement> {
    return {
        interstitial_betwon_dialog: { enabled: false, cooldownMinutes: 0 },
        interstitial_minigame: { enabled: false, cooldownMinutes: 0, everyNWins: 3 },
        interstitial_dailybonus_close_dialog: { enabled: false, cooldownMinutes: 0 },
        interstitial_battle_result: { enabled: false, cooldownMinutes: 0 },
        interstitial_flashbet_round_end: { enabled: false, cooldownMinutes: 0 },
    };
}

function placement(
    enabled: boolean,
    cooldownMinutes: number,
    everyNWins?: number,
): InterstitialPolicyPlacement {
    return everyNWins == null
        ? { enabled, cooldownMinutes }
        : { enabled, cooldownMinutes, everyNWins };
}

function segment(
    id: string,
    includeTags: string[],
    global: InterstitialPolicyGlobal,
    betWonMinutes: number,
    everyNWins: number,
): InterstitialPolicyRule {
    return {
        id,
        enabled: true,
        includeTags,
        excludeTags: [],
        interstitialsEnabled: true,
        combineWithOthers: false,
        global,
        placements: {
            interstitial_betwon_dialog: placement(true, betWonMinutes),
            interstitial_minigame: placement(true, 0, everyNWins),
            interstitial_dailybonus_close_dialog: placement(false, 0),
            interstitial_battle_result: placement(false, 0),
            interstitial_flashbet_round_end: placement(false, 0),
        },
    };
}

/** Fills the editor only. Nothing is live until Save. */
export function recommendedInterstitialPolicies(): InterstitialPolicyDocument {
    const quiet = (gap: number, dailyCap: number, graceMinutes = 0, requireFirstBet = false): InterstitialPolicyGlobal => ({
        minGapSeconds: gap,
        maxPerSession: 0,
        maxPerDay: dailyCap,
        signupGraceMinutes: graceMinutes,
        requireFirstBet,
        rewardedQuietMinutes: 10,
    });
    return {
        version: 1,
        globalEnabled: true,
        experiment: { enabled: true, holdoutPercent: 20, salt: 'int_seg_2026_10' },
        defaultPolicyId: 'default',
        policies: [
            {
                id: 'purchasers',
                enabled: true,
                includeTags: ['Android_Purchasers'],
                excludeTags: [],
                interstitialsEnabled: false,
            },
            segment('day_one', ['int_day_one'], quiet(180, 6, 60, true), 10, 5),
            segment('parasites', ['parasites'], quiet(45, 0), 2, 2),
            segment('high_ecpm', ['int_high_ecpm'], quiet(45, 0), 3, 2),
            segment('low_ecpm', ['int_low_ecpm'], quiet(180, 15), 10, 5),
            segment('default', [], quiet(60, 0), 5, 3),
        ],
    };
}
